import type { ReactNode } from 'react';
import { Box } from '@chakra-ui/react';
import {
  Tag,
  Text,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@kula-ai/components';
import { Lock01 } from '@kula-ai/icons-core';
import { colors } from '@/theme/foundations/colors';
import {
  formulaSegments,
  SEGMENT_COLOR,
} from '@/utils/formula/formulaHighlight';

const FORMULA_TOOLTIP_LABEL = 'Current formula:';
const FORMULA_TAG_LABEL = 'Formula';

// The same colouring the settings editor uses, so one formula reads the same
// in both places.
const HighlightedFormula = ({ formula }: { formula: string }) => (
  <Box
    mt="1"
    px="2"
    py="1"
    bg="secondary-25"
    borderWidth="1px"
    borderColor="border-color"
    borderRadius="md"
    fontSize="xs"
    // The design's formula is nested and indented, so both must survive.
    whiteSpace="pre-wrap"
    wordBreak="break-word"
    data-test-id="calculated-formula"
  >
    {formulaSegments(formula, []).map((segment) => (
      <span
        key={`${segment.start}-${segment.text}`}
        style={{ color: SEGMENT_COLOR[segment.kind] }}
      >
        {segment.text}
      </span>
    ))}
  </Box>
);

// The formula behind the value, so the recruiter can see what produced it.
// It hangs off the tag rather than the whole field: the tag is what says the
// value is calculated, so that is what you point at to ask how.
const FormulaTooltip = ({
  formula,
  children,
}: {
  formula: string;
  children: ReactNode;
}) => (
  // 700ms default reads as broken, so the repo pins hover tooltips to 150ms.
  <TooltipProvider delayDuration={150}>
    <Tooltip>
      <TooltipTrigger asChild>
        {/* The tag is not focusable on its own, so the wrapper takes the tab
            stop — the formula has to be reachable without a mouse. */}
        <Box
          as="span"
          display="inline-flex"
          tabIndex={0}
          data-test-id="calculated-field-trigger"
        >
          {children}
        </Box>
      </TooltipTrigger>
      <TooltipContent variant="light" side="top" collisionPadding={12}>
        <Box maxW="320px">
          <Text variant="small">{FORMULA_TOOLTIP_LABEL}</Text>
          <HighlightedFormula formula={formula} />
        </Box>
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

export interface FormulaLabelTagProps {
  formula: string;
  // Locked shows the padlock: the value is the formula's, not the filler's.
  locked?: boolean;
  // A rule that cannot compute, or a result the field cannot hold.
  invalid?: boolean;
}

// Primary on the normal path, so the tag reads as the feature's own mark
// rather than a neutral chip. Red on the error path; the repo's red is the
// pastel-1 pair, not an error scale.
export const FormulaLabelTag = ({
  formula,
  locked = true,
  invalid = false,
}: FormulaLabelTagProps) => (
  <FormulaTooltip formula={formula}>
    <Tag
      size="sm"
      label={FORMULA_TAG_LABEL}
      // The lock takes the label's colour, so it is passed explicitly: an
      // icons-core icon is a bare svg and a token name means nothing to it.
      leftElement={
        locked ? (
          <Lock01
            size={12}
            color={invalid ? colors['pastel-1-dark'] : colors['primary-600']}
          />
        ) : undefined
      }
      // Tag spreads `style` after its own, so `background` must go here too.
      style={
        invalid
          ? {
              background: colors['pastel-1-light'],
              color: colors['pastel-1-dark'],
            }
          : {
              background: colors['primary-50'],
              color: colors['primary-600'],
            }
      }
      data-test-id="calculated-formula-tag"
    />
  </FormulaTooltip>
);
